import React from "react"
import { cn } from "@/lib/utils"
import { useNavigate, useLocation } from "react-router-dom"
import { Business } from "@/schemas/business"; // Changed to Zod-inferred type
import { useIsMobile } from "@/hooks/use-mobile"
import { NavBarMainRow } from "./NavBarMainRow"
import { NavBarMobileRows } from "./NavBarMobileRows"
import { TimeFrameOption } from "@/components/business/leads/utils/dateFilters"
// import { useDataUtils } from "@/hooks/useDataUtils"; // Removed
import { FEATURE_FLAGS } from '@/config/featureFlag';
// FirebaseProvider will provide businesses, so direct useFirebase might not be needed here if allBusinesses is passed as prop

interface NavBarProps {
  className?: string
  businessName?: string
  timeFrame?: TimeFrameOption | string
  onTimeFrameChange?: (value: TimeFrameOption) => void
  allBusinesses?: Business[]
  onBusinessSelect?: (businessId: string) => void
  portfolioDropdown?: boolean
  selectedPortfolio?: string
  portfolioOptions?: Array<{
    id: string
    title?: string
    name?: string
    role?: string | null
  }>
  onPortfolioChange?: (value: string) => void
  handleEditCampaign?: () => void
  handleEditGoPlan?: () => void
  handleEditGoCampaign?: () => void
  handleNavigateToSettings?: () => void
  isGoCampaignPage?: boolean
}

const NavBar: React.FC<NavBarProps> = ({
  className,
  businessName,
  timeFrame = "Past 30 days",
  onTimeFrameChange,
  allBusinesses = [],
  onBusinessSelect,
  portfolioDropdown = false,
  selectedPortfolio = "",
  portfolioOptions = [],
  onPortfolioChange,
  handleEditCampaign,
  handleEditGoPlan,
  handleEditGoCampaign,
  handleNavigateToSettings,
  isGoCampaignPage = false,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const isBusinessPage = location.pathname.includes("/business/")
  const isBusinessSettingsPage = location.pathname.includes("/settings")
  const isIndexPage = location.pathname === "/"
  const isGoCampaignPagePath =
    isGoCampaignPage || location.pathname.includes("/gocampaign")
  const isEditPage = location.pathname.includes("/edit")
  // const { getPortfolios } = useDataUtils() // Removed

  // effectiveBusinesses should now directly be allBusinesses prop,
  // as FirebaseProvider is the source of truth passed down.
  const effectiveBusinesses = allBusinesses;

  const defaultEditCampaign = () => {
    console.log("Default handleEditCampaign called in NavBar")
    const pathParts = location.pathname.split("/")
    const businessIdIndex = pathParts.indexOf("business") + 1

    if (businessIdIndex > 0 && businessIdIndex < pathParts.length && FEATURE_FLAGS.ONBOARDING) {
      const businessId = pathParts[businessIdIndex]
      console.log(
        `Navigating to /business/${businessId}/gocampaign using React Router`
      )
      navigate(`/business/${businessId}/gocampaign`, { replace: true })
    } else {
      console.error(
        "Could not extract business ID from path:",
        location.pathname
      )
    }
  }

  const defaultEditGoPlan = () => {
    console.log("Default handleEditGoPlan called in NavBar")
    const pathParts = location.pathname.split("/")
    const businessIdIndex = pathParts.indexOf("business") + 1

    if (businessIdIndex > 0 && businessIdIndex < pathParts.length) {
      const businessId = pathParts[businessIdIndex]
      console.log(
        `Navigating to /business/${businessId}/gocampaign?step=0 using React Router`
      )
      navigate(`/business/${businessId}/gocampaign?step=0`, { replace: true })
    } else {
      console.error(
        "Could not extract business ID from path:",
        location.pathname
      )
    }
  }

  const defaultEditGoCampaign = () => {
    console.log("Default handleEditGoCampaign called in NavBar")
    const pathParts = location.pathname.split("/")
    const businessIdIndex = pathParts.indexOf("business") + 1

    if (businessIdIndex > 0 && businessIdIndex < pathParts.length && FEATURE_FLAGS.ONBOARDING) {
      const businessId = pathParts[businessIdIndex]
      console.log(
        `Navigating to /business/${businessId}/gocampaign?step=1 using React Router`
      )
      navigate(`/business/${businessId}/gocampaign?step=1`, { replace: true })
    } else {
      console.error(
        "Could not extract business ID from path:",
        location.pathname
      )
    }
  }

  const defaultNavigateToSettings = () => {
    console.log("Default handleNavigateToSettings called in NavBar")
    const pathParts = location.pathname.split("/")
    const businessIdIndex = pathParts.indexOf("business") + 1
    if (businessIdIndex > 0 && businessIdIndex < pathParts.length && FEATURE_FLAGS.SETTINGS) {
      const businessId = pathParts[businessIdIndex]
      console.log(
        `Navigating to /business/${businessId}/settings using React Router`
      )
      navigate(`/business/${businessId}/settings`, { replace: true })
    }
  }

  const handleNavigation = (id: string) => {
    console.log("NavBar - handleNavigation called with id:", id)

    if (onBusinessSelect) {
      console.log("Using onBusinessSelect callback from NavBar props")
      onBusinessSelect(id)
    } else {
      if (id === "home") {
        console.log("Navigating to home using React Router")
        navigate("/")
      } else {
        const currentPath = location.pathname

        if (currentPath.includes("/business/")) {
          const newPath = currentPath.replace(
            /\/business\/[^/]+/,
            `/business/${id}`
          )

          console.log(`Navigating to: ${newPath}${location.search}`)
          navigate(`${newPath}${location.search}`, { replace: true })
        } else {
          console.log(
            "Fallback navigation to business dashboard:",
            `/business/${id}`
          )
          navigate(`/business/${id}`)
        }
      }
    }
  }

  return (
    <header
      className={cn(
        "w-full bg-white border-b border-border flex flex-col sticky top-0 z-50",
        className
      )}
    >
      <NavBarMainRow
        businessName={businessName}
        timeFrame={timeFrame}
        onTimeFrameChange={onTimeFrameChange}
        allBusinesses={effectiveBusinesses}
        effectiveBusinesses={effectiveBusinesses}
        handleNavigation={handleNavigation}
        isBusinessPage={isBusinessPage}
        isBusinessSettingsPage={isBusinessSettingsPage}
        navigate={navigate}
        isMobile={isMobile}
        portfolioDropdown={portfolioDropdown}
        selectedPortfolio={selectedPortfolio}
        portfolioOptions={portfolioOptions}
        onPortfolioChange={onPortfolioChange}
        isGoCampaignPage={isGoCampaignPagePath}
        isEditPage={isEditPage}
        handleEditCampaign={handleEditCampaign || defaultEditCampaign}
        handleEditGoPlan={handleEditGoPlan || defaultEditGoPlan}
        handleEditGoCampaign={handleEditGoCampaign || defaultEditGoCampaign}
        handleNavigateToSettings={
          handleNavigateToSettings || defaultNavigateToSettings
        }
      />

      {isMobile && !isIndexPage && (
        <NavBarMobileRows
          businessName={businessName}
          allBusinesses={effectiveBusinesses}
          handleNavigation={handleNavigation}
          portfolioDropdown={portfolioDropdown}
          selectedPortfolio={selectedPortfolio}
          portfolioOptions={portfolioOptions}
          onPortfolioChange={onPortfolioChange}
        />
      )}
    </header>
  )
}

export default NavBar
